import { useCallback, useEffect, useRef } from 'react';
import { useGameStore } from '../stores/gameStore';
import type { ClientMessage, ServerMessage, GambitState } from '@wasted-games/shared';

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnects = 5;

  const {
    setConnected,
    setError,
    setPlayers,
    addPlayer,
    removePlayer,
    updateState,
    setWinner,
    setConnection,
  } = useGameStore();

  const connect = useCallback(
    (code: string, pid: string, isHost: boolean) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      setConnection(code, pid, isHost);

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/session/${code}/ws`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        // Send hello
        const msg: ClientMessage = { type: 'hello', playerId: pid };
        ws.send(JSON.stringify(msg));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as ServerMessage;
          handleMessage(msg);
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setConnected(false);
        // Try reconnect
        if (reconnectAttempts.current < maxReconnects) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
          reconnectAttempts.current++;
          setTimeout(() => {
            connect(code, pid, isHost);
          }, delay);
        }
      };

      ws.onerror = () => {
        setError('Connection error');
      };
    },
    [setConnection, setConnected, setError]
  );

  const handleMessage = useCallback(
    (msg: ServerMessage) => {
      switch (msg.type) {
        case 'init':
          setConnected(true);
          setPlayers(msg.players);
          updateState(msg.state as GambitState);
          break;
        case 'state_update':
          updateState(msg.state as GambitState);
          break;
        case 'player_joined':
          addPlayer(msg.player);
          break;
        case 'player_left':
          removePlayer(msg.playerId);
          break;
        case 'game_ended':
          setWinner(msg.winner);
          updateState(msg.finalState as GambitState);
          break;
        case 'error':
          setError(msg.message);
          // Clear error after 3 seconds
          setTimeout(() => setError(null), 3000);
          break;
        case 'pong':
          break;
      }
    },
    [setConnected, setPlayers, addPlayer, removePlayer, updateState, setWinner, setError]
  );

  const send = useCallback((msg: ClientMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const disconnect = useCallback(() => {
    reconnectAttempts.current = maxReconnects; // prevent reconnect
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  // Heartbeat
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  return { connect, send, disconnect };
}
